/* import __COLOCATED_TEMPLATE__ from './dialog-blade.hbs'; */
/**
## Blade

Blades are used to **view, compare, and switch content quickly while maintaining the majority of the page view**.
Like sheets, all blades should have their own route, and are opened/closed by transition to/from that route.
All blades have a header with a title, and an `x` on the left which will close the blade. A blade's footer buttons are optional.

Blades should not have editable fields directly inside. To edit content, a modal or sheet should be opened.

| Description | Contextual Component |
| --- | --- |
| Header | [dialog-header](/components/glass/dialog-header.html) |
| Title | [dialog-header/title](/components/glass/dialog-header/title.html) |
| Dropdown | [sq-dropdown](./sq-dropdown.html) |
| Body | [dialog-body](/components/glass/dialog-body.html) |
| Actions | [dialog-actions](/components/glass/dialog-actions.html) |
| Button | [dialog-actions/button](/components/glass/dialog-actions/button.html) |

```example
@title Example with Footer Buttons
@description Card styles are meant to be used within blades. Check out the <a href="/components/glass/sq-card.html">documentation on cards</a>.

This example has a footer button wrapped in a dropdown to show more options, and a footer button with an action to close the blade.

A blade footer is optional.

@controller-properties
{
  showBlade: false,
  actions: {
    // The below example shows the correct way that blades should be closed -
    // with a link-to component passed into `close`.

    // This function is only for the purpose of this fuse-docs example,
    // and is used in the footer so that the blade can be closed in fuse-docs.
    close() {
      this.set('showBlade', false);
    }
  }
}

@hbs
{{#toggle-code-example exampleName="Blade" show=showBlade}}
  {{#dialog-blade title="Blade Title" close=(component "link-to" route="index") as |blade|}}
    {{#blade.body}}
      {{#sq-card context="BLADE" as |card|}}
        {{card.header title="Notes"
                      link=(hash href="http://example.com/"
                                 text="Add Note")}}
        {{#card.body}}
          Notes go here...
        {{/card.body}}
        {{#card.footer as |cardFooter|}}
          {{cardFooter.button-secondary label="View All"}}
        {{/card.footer}}
      {{/sq-card}}
    {{/blade.body}}
    {{#blade.footer as |footer|}}
      {{footer.button label="Close Blade" click=(action "close")}}
      {{#sq-dropdown class="dialog--blade__footer__button--with-dropdown" as |dropdown|}}
        {{footer.button label="Open Dropdown"
                        click=dropdown.toggle}}
          {{#dropdown.popover class="popover--fly-up"}}
          <div class="popover-list__item">Popover Item</div>
        {{/dropdown.popover}}
      {{/sq-dropdown}}
    {{/blade.footer}}
  {{/dialog-blade}}
{{/toggle-code-example}}
```

```example
@title Customizing the Title
@description <strong>This is highly discouraged and should be rarely needed. 98% of the time, you can just
pass a string to the <code>title</code> param.</strong> You can pass a <code>{{#blade.title}}</code> block if you need to pass formatted HTML
to the title area for some reason. Make sure to remove the <code>title</code> param from the parent <code>dialog-blade</code>
component as this param will override anything you pass to the block.

@controller-properties
{
  showBlade: false,
  actions: {
    // The below example shows the correct way that blades should be closed -
    // with a link-to component passed into `close`.

    // This function is only for the purpose of this fuse-docs example,
    // and is used in the footer so that the blade can be closed in fuse-docs.
    close() {
      this.set('showBlade', false);
    }
  }
}

@hbs
{{#toggle-code-example exampleName="Blade" show=showBlade}}
  {{#dialog-blade close=(component "link-to" route="index") as |blade|}}
    {{#blade.title}}
      <span class="online-status-indicator online-status-indicator--online">Online</span> Taylor Swift
    {{/blade.title}}
    {{#blade.body}}
      {{#sq-card context="BLADE" as |card|}}
        {{card.header title="Notes"
                      link=(hash href="http://example.com/"
                                 text="Add Note")}}
        {{#card.body}}
          Notes go here...
        {{/card.body}}
        {{#card.footer as |cardFooter|}}
          {{cardFooter.button-secondary label="View All"}}
        {{/card.footer}}
      {{/sq-card}}
    {{/blade.body}}
    {{#blade.footer as |footer|}}
      {{footer.button label="Close Blade" click=(action "close")}}
      {{#sq-dropdown class="dialog--blade__footer__button--with-dropdown" as |dropdown|}}
        {{footer.button label="Open Dropdown"
                        click=dropdown.toggle}}
          {{#dropdown.popover class="popover--fly-up"}}
          <div class="popover-list__item">Popover Item</div>
        {{/dropdown.popover}}
      {{/sq-dropdown}}
    {{/blade.footer}}
  {{/dialog-blade}}
{{/toggle-code-example}}
```

```example
@title Example with Close Action instead of Close Link-to Component
@description Note that this method should only be used if a route transition with a `link-to` component cannot handle the blade closure.

@controller-properties
{
  showBlade: false,
  actions: {
    // Closing a blade requires a route transition action and can be inlined in the template as
    // close=(component "link-to" route="index") like in the above examples or done in an action with the
    // this.transitionToRoute('/') function

    // for the purpose of this code example were just toggling it being shown.
    close() {
      this.set('showBlade', false);
      // this.transitionToRoute('/');
    }
  }
}

@hbs
{{#toggle-code-example exampleName="Blade" show=showBlade}}
  {{#dialog-blade title="Blade Title" close=(action "close") as |blade|}}
    {{#blade.body}}
      body of blade
    {{/blade.body}}
  {{/dialog-blade}}
{{/toggle-code-example}}
```

## How to show a blade

* Create a template based on one of the examples above (or `templates/cash-drawers/show.hbs`).

* Create a controller/component (example: `controllers/cash-drawers/show.js`) for the blade to live in. Set all necessary variables, actions, etc for the blade's content..

* Register your blade as a route in `dashboard/app/router.js` or an apps individual router file (example: `dashboard/app/router/account-settings-routes.js`).

```js
this.route('cash_drawers', { path: '/cash-drawers' }, function() {
  this.route('show', { path: '/:clientCashDrawerShiftToken' });
});
```

* To open your blade, call an action which transitions to its route (example: to open the cash-drawers show blade, you would call `this.transitionTo('cash_drawers.show', shiftToken);`).

* Congrats! You should have a working blade!

### Params

@param {String} title - Localized string. Shown in the middle of the blade header.
@param {Action/Component} close - Action/component used for the `x` in the header which closes the blade.
@param {Boolean} [showAtStartingPosition=true]

Style dependencies at: `@square/glass-ui/addon/styles/@square/dialog/blade`
*/

import { assert } from '@ember/debug';
import Component from '@ember/component';
import { readOnly } from '@ember/object/computed';
import { next } from '@ember/runloop';
import { inject as service } from '@ember/service';

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend({
  flash: service(),
  title: null,
  attributeBindings: ['data-test-blade'],
  'data-test-blade': readOnly('title'),
  classNames: ['dialog', 'dialog--blade', 'ecosystem-header-top-element'],
  classNameBindings: ['showAtStartingPosition:dialog--blade--starting-position'],
  ariaRole: 'dialog',
  showAtStartingPosition: true,
  didInsertElement(...args) {
    this._super(...args);
    next(() => {
      if (this.get('isDestroyed') || this.get('isDestroying')) {
        return;
      }
      this.set('showAtStartingPosition', false);
    });
  },
  didReceiveAttrs() {
    this._super();
    if (this.get('close')) {
      assert('Optional `close` parameter should be an action or link-to component in `{{dialog-blade}}.`', typeof this.get('close') === 'function' || typeof this.get('close') === 'object');
    }
  }
});